import { useEffect, useRef } from "react";

export type UseCommandPaletteShortcutsProps = {
  onToggle?: () => void;
  onClose?: () => void;
  doubleShiftMaxDelay?: number;
};

export const useCommandPaletteShortcuts = ({
  onToggle,
  onClose,
  doubleShiftMaxDelay = 400,
}: UseCommandPaletteShortcutsProps) => {
  const lastShiftPressTimeRef = useRef(0);

  useEffect(() => {
    const handleDoubleShift = (event: KeyboardEvent): boolean => {
      if (event.repeat || event.key !== "Shift") {
        // Reset double shift timer if another key is pressed (and it's not Shift itself)
        if (event.key !== "Shift") {
          lastShiftPressTimeRef.current = 0;
        }
        return false;
      }

      const currentTime = Date.now();
      const timeDifference = currentTime - lastShiftPressTimeRef.current;

      if (timeDifference <= doubleShiftMaxDelay) {
        onToggle?.();
        lastShiftPressTimeRef.current = 0;
        return true;
      } else {
        lastShiftPressTimeRef.current = currentTime;
        return false;
      }
    };

    const handleToggleShortcut = (event: KeyboardEvent): boolean => {
      const isKKey = event.code === "KeyK" || event.key.toLowerCase() === "k";
      const isPKey = event.code === "KeyP" || event.key.toLowerCase() === "p";
      const modifierKeyPressed = event.ctrlKey || event.metaKey;
      const isShiftPressed = event.shiftKey;

      const isKShortcut = isKKey && modifierKeyPressed && !isShiftPressed;
      const isPShortcut = isPKey && modifierKeyPressed && isShiftPressed;

      if (isKShortcut || isPShortcut) {
        if (onToggle) {
          event.preventDefault();
          onToggle();
        }
        return true;
      }
      return false;
    };

    const handleCloseShortcut = (event: KeyboardEvent): boolean => {
      if (event.key === "Escape") {
        onClose?.();
        return true;
      }
      return false;
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      // Order matters: Check double shift first, as it involves only the Shift key.
      if (handleDoubleShift(event)) {
        return;
      }

      if (handleToggleShortcut(event)) {
        return;
      }

      if (handleCloseShortcut(event)) {
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onToggle, onClose, doubleShiftMaxDelay]);
};
